// <cart-items section-id="…">: the bag's line list, used by both the drawer and the cart page.
// Quantity, remove and upsell buttons work by line key; the list re-renders itself after any cart change.

import { getCart, updateLines, addItems, bundleUpdates, fetchSection } from '@theme/cart-api';
import { hydrateBubbles } from '@theme/bubble';
import { toast, debounce } from '@theme/utils';

class CartItems extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.onClick);
    this.addEventListener('change', this.onQuantityInput);
    this.onCartChanged = this.refresh.bind(this);
    document.addEventListener('cart:changed', this.onCartChanged);
    hydrateBubbles(this);
  }

  disconnectedCallback() {
    document.removeEventListener('cart:changed', this.onCartChanged);
  }

  onClick = async (event) => {
    const button = event.target.closest('[data-quantity-step], [data-remove], [data-upsell]');
    if (!button) return;
    event.preventDefault();
    if (button.dataset.upsell) return this.addUpsell(button);
    const key = button.closest('[data-line-key]')?.dataset.lineKey;
    if (!key) return;
    const current = Number(button.closest('[data-line-key]').dataset.quantity || 1);
    const quantity = button.hasAttribute('data-remove') ? 0 : Math.max(0, current + Number(button.dataset.quantityStep));
    this.setLine(key, quantity);
  };

  onQuantityInput = debounce((event) => {
    const input = event.target.closest('[data-quantity-input]');
    const key = input?.closest('[data-line-key]')?.dataset.lineKey;
    if (key) this.setLine(key, Math.max(0, Number(input.value) || 0));
  }, 400);

  async setLine(key, quantity) {
    this.setAttribute('aria-busy', 'true');
    try {
      const cart = await getCart();
      await updateLines(bundleUpdates(cart, key, quantity));
    } catch (error) {
      toast(error.message);
      this.removeAttribute('aria-busy');
    }
  }

  async addUpsell(button) {
    button.disabled = true;
    try {
      await addItems([{ id: Number(button.dataset.upsell), quantity: 1 }]);
      toast(`${button.dataset.title || 'Extra'} added`);
    } catch (error) {
      toast(error.message);
      button.disabled = false;
    }
  }

  async refresh(event) {
    const fresh = await fetchSection(this.getAttribute('section-id')).catch(() => null);
    const next = fresh?.querySelector('cart-items');
    if (next) {
      this.innerHTML = next.innerHTML;
      hydrateBubbles(this);
    }
    // Other parts of the same section that depend on the cart (totals, footers) opt in with data-refresh-id.
    fresh?.querySelectorAll('[data-refresh-id]').forEach((part) => {
      const current = document.querySelector(`[data-refresh-id="${part.dataset.refreshId}"]`);
      if (current) current.innerHTML = part.innerHTML;
    });
    this.removeAttribute('aria-busy');
    const count = event?.detail?.cart?.item_count;
    if (count !== undefined) {
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = count;
        el.dataset.count = count;
      });
    }
    this.dispatchEvent(new CustomEvent('cart-items:rendered', { bubbles: true }));
  }
}

customElements.define('cart-items', CartItems);
